import { useId } from "react";

interface ChoiceFlashArrowProps {
  color: string;
  rotation?: number;
  scale?: number;
  opacity?: number;
}

/**
 * Lightweight double-chevron indicator — highway-style dotted arrows
 * with alternating flash. Only shown when a scene has explicit choices.
 */
export function ChoiceFlashArrow({
  color,
  rotation = 0,
  scale = 1,
  opacity = 1,
}: ChoiceFlashArrowProps) {
  const id = useId().replace(/:/g, "");

  return (
    <svg
      viewBox="0 0 40 48"
      style={{
        width: "100%",
        height: "100%",
        transform: `rotate(${rotation}deg) scale(${scale})`,
        opacity,
        overflow: "visible",
      }}
      aria-hidden
    >
      <defs>
        <style>{`
          @keyframes ${id}-a {
            0%, 45% { opacity: 1; }
            50%, 95% { opacity: 0.2; }
            100% { opacity: 1; }
          }
          @keyframes ${id}-b {
            0%, 45% { opacity: 0.2; }
            50%, 95% { opacity: 1; }
            100% { opacity: 0.2; }
          }
          .${id}-chev-a { animation: ${id}-a 0.7s ease-in-out infinite; }
          .${id}-chev-b { animation: ${id}-b 0.7s ease-in-out infinite; }
        `}</style>
      </defs>

      {/* Upper chevron */}
      <polyline
        className={`${id}-chev-a`}
        points="8,18 20,8 32,18"
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="3 4"
      />

      {/* Lower chevron */}
      <polyline
        className={`${id}-chev-b`}
        points="8,32 20,22 32,32"
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="3 4"
      />

      {/* Subtle glow */}
      <polyline
        points="8,32 20,22 32,32"
        fill="none"
        stroke={color}
        strokeWidth="6"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={0.08}
      />
    </svg>
  );
}
